import { ruleGroupsSchema } from "@/components/apply-to/rule-groups.schema.ts";
import { getPerformanceReviewParticipantGroups } from "@/types/performance-review.types.ts";
import { RuleGroupsEditor } from "@/components/apply-to/RuleGroupsEditor.tsx";
import { Datepicker } from "@/components/datepicker/Datepicker.tsx";
import { emojiDefaults } from "@/components/emoji/emoji.type.ts";
import { EmojiPickerDropdown } from "@/components/emoji/EmojiPickerDropdown.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { PerformanceReviewEditSidebar } from "@/pages/talent/performance-reviews/edit/components/PerformanceReviewEditSidebar.tsx";
import {
  PerformanceReviewEditPageProps,
  usePerformanceReviewEditPage,
} from "@/pages/talent/performance-reviews/edit/pages/performance-review-edit-pages.tsx";
import { PerformanceReviewTitleSlot } from "@/pages/talent/performance-reviews/edit/pages/PerformanceReviewTitleSlot.tsx";
import { PerformanceReviewEditPageStep } from "@/pages/talent/performance-reviews/edit/performance-review-edit.ts";
import { useEmployeeMe, useEmployees } from "@/queries/employees/use-employees.query.ts";
import { usePerformanceReviewUpdateDetails } from "@/queries/performance-reviews/use-performance-reviews.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiEmployeeFilter, EmployeeFilterDefault } from "@/types/employees/employee.types.ts";
import { RuleRegistryPlace } from "@/types/rule-whitelists.ts";
import { useRuleConditionTypes } from "@/queries/use-rules.query.ts";
import { ApiFeatureType } from "@/types/subscription.types.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Avatar,
  Box,
  Button,
  FontAwesomeIcon,
  FullScreenPage,
  Input,
  Multiselect,
  SelectItem,
  Stack,
  Switch,
  Textarea,
  Title,
} from "@skailer-com/uikit";
import { useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

export const PerformanceReviewEditDetailsPage = ({ review }: PerformanceReviewEditPageProps) => {
  const { t } = useTranslation();
  const participantConditionTypes = useRuleConditionTypes(
    RuleRegistryPlace.performanceReviewParticipants
  );

  const performanceReviewEditDetailsSchema = z
    .object({
      emoji: z.string().nullable(),
      title: z.string().min(1, t("errors:field_required")),
      description: z.string().nullable(),
      owner_ids: z.array(z.number()).optional(),
      use_ai: z.boolean().optional(),
      participants_rule_groups: ruleGroupsSchema(),
      date_starts_at: z.date().nullable(),
      date_ends_at: z.date().nullable(),
    })
    .refine(
      (data) =>
        !data.date_starts_at || !data.date_ends_at || data.date_ends_at >= data.date_starts_at,
      { path: ["date_ends_at"], message: t("errors:end_date_should_be_greater_start_date") }
    );

  const { confirmExit, navigateToStep } = usePerformanceReviewEditPage();
  const { renderEmployeeName } = useFormatter();
  const { isFeatureEnabled } = usePermissions();

  const { data: employeeMe } = useEmployeeMe();

  const canManageOwners = useMemo(() => {
    if (!employeeMe?.id || !review.owners?.[0]) return false;

    return review.owners[0].id === employeeMe.id;
  }, [employeeMe, review]);

  const ownerOptions = useMemo(() => {
    if (!review.owners || !employeeMe) return [];

    return review.owners
      .filter((x) => x.id !== employeeMe.id)
      .map((x) => ({
        id: x.id,
        title: renderEmployeeName(x),
      })) as SelectItem[];
  }, [review, employeeMe]);

  const [useInviteCoOwners, setUseInviteCoOwners] = useState<boolean>(ownerOptions.length > 0);

  const {
    control,
    register,
    handleSubmit,
    setValue,
    formState: { errors, isDirty },
  } = useForm({
    mode: "onSubmit",
    resolver: zodResolver(performanceReviewEditDetailsSchema),
    values: {
      emoji: review.emoji || emojiDefaults.performanceReview.default,
      title: review.title,
      description: review.description,
      owner_ids: ownerOptions.map((x) => x.id as number),
      use_ai: review.use_ai ?? false,
      participants_rule_groups: getPerformanceReviewParticipantGroups(review).map((group) => ({
        rules: group.rules.map((rule) => ({ ...rule, value_ids: rule.value_ids ?? [] })),
      })),
      date_starts_at: review.date_starts_at,
      date_ends_at: review.date_ends_at,
    },
  });

  const [useEmoji, setUseEmoji] = useState(!!review.emoji);
  const reviewUpdate = usePerformanceReviewUpdateDetails();

  const onSubmit = handleSubmit(async (data) => {
    const initialUseEmoji = !!review.emoji;
    const initialUseInviteCoOwners = ownerOptions.length > 0;
    const initialIsTimeFrame = review.date_ends_at !== null;

    const hasToggleChanges =
      useEmoji !== initialUseEmoji ||
      useInviteCoOwners !== initialUseInviteCoOwners ||
      isTimeFrame !== initialIsTimeFrame;

    const shouldSave = isDirty || hasToggleChanges;

    if (!shouldSave) {
      navigateToStep(review.id, PerformanceReviewEditPageStep.reviewers);
      return;
    }

    try {
      await reviewUpdate.mutateAsync({
        id: review.id,
        payload: {
          ...data,
          participants_rule_groups: data.participants_rule_groups.filter(
            (group) => group.rules.length > 0
          ),
          use_ai: data.use_ai ?? false,
          emoji: useEmoji ? data.emoji : "",
          owner_ids: canManageOwners ? (useInviteCoOwners ? data.owner_ids : []) : undefined,
        },
      });
      analyticsService.trackEvent(analyticEvents.performanceReviews.edited, {
        [analyticProperties.id]: review.id,
        [analyticProperties.tab]: PerformanceReviewEditPageStep.details,
      });
      navigateToStep(review.id, PerformanceReviewEditPageStep.reviewers);
    } catch {}
  });

  const [employeeOwnersFilter, setEmployeeOwnersFilter] = useState<ApiEmployeeFilter>(() => ({
    ...EmployeeFilterDefault,
    exclude_ids: employeeMe?.id ? [employeeMe.id] : [],
  }));

  const { data: employeesOwnersSearch, isLoading: isEmployeesOwnerIsLoading } = useEmployees(
    {
      ...employeeOwnersFilter,
      exclude_ids: employeeMe?.id ? [employeeMe.id] : [],
    },
    canManageOwners
  );

  const employeesOwnersSearchOptions = useMemo(() => {
    if (employeesOwnersSearch?.items) {
      return employeesOwnersSearch.items.map((x) => {
        return { id: x.id, title: renderEmployeeName(x) };
      }) as SelectItem[];
    }
    return [] as SelectItem[];
  }, [employeesOwnersSearch, renderEmployeeName]);

  const [isTimeFrame, setIsTimeFrame] = useState<boolean>(review.date_ends_at !== null);

  return (
    <FullScreenPage
      backSlot={
        <Button variant={"secondary"} size={"lg"} onClick={() => confirmExit(review.id)}>
          {t("common:exit")}
        </Button>
      }
      actionsSlot={
        <>
          <Button size={"lg"} onClick={onSubmit}>
            {t("common:continue")}
          </Button>
        </>
      }
      titleSlot={<PerformanceReviewTitleSlot step={PerformanceReviewEditPageStep.details} />}
      isCenterVertical={false}
      sidebarSlot={
        <PerformanceReviewEditSidebar
          step={PerformanceReviewEditPageStep.details}
          review={review}
        />
      }
    >
      <form onSubmit={onSubmit}>
        <Stack gap={"sm"} justify={"center"}>
          <Stack className={"card-panel"} gap={"2xl"}>
            {useEmoji && (
              <Stack>
                <Controller
                  control={control}
                  name={"emoji"}
                  render={({ field: { value, onChange } }) => (
                    <EmojiPickerDropdown
                      onEmojiSelect={(em) => {
                        onChange(em);
                      }}
                    >
                      <Box
                        className={
                          "relative w-41 h-41 group cursor-pointer rounded-md bg-brand-dark/5 shadow-lg"
                        }
                      >
                        {value && (
                          <Avatar variant={"plain"} url={undefined} emoji={value} size={"2xl"} />
                        )}
                        <Box
                          className={
                            "bg-dark/20 opacity-0 group-hover:opacity-100 absolute inset-0 rounded-md flex items-center justify-center"
                          }
                        >
                          <FontAwesomeIcon
                            icon={"fa-light fa-icons"}
                            className={"text-[74px] text-light "}
                          />
                        </Box>
                      </Box>
                    </EmojiPickerDropdown>
                  )}
                />

                {errors.emoji && (
                  <Box className="pt-2 pb-4">
                    <span className="text-danger">{errors.emoji.message}</span>
                  </Box>
                )}
              </Stack>
            )}

            <Stack gap={"lg"}>
              <Input
                type={"text"}
                label={t("common:title")}
                size={"lg"}
                {...register("title")}
                error={errors.title?.message}
                required
              />
              <Textarea
                label={t("common:description")}
                {...register("description")}
                error={errors.description?.message}
              />
              <Switch
                checked={!useEmoji}
                onChange={(value) => {
                  setUseEmoji(!value);
                }}
                title={t("performance_review:do_not_use_emoji")}
              />
              {isFeatureEnabled(ApiFeatureType.talent_performance_reviews_ai) && (
                <Controller
                  control={control}
                  name={"use_ai"}
                  render={({ field: { value, onChange } }) => (
                    <Switch
                      checked={!!value}
                      onChange={onChange}
                      title={t("performance_review:use_ai_title")}
                      description={t("performance_review:use_ai_description")}
                    />
                  )}
                />
              )}
            </Stack>
          </Stack>
          <Stack className={"card-panel"} gap={"2xl"}>
            <Title header={t("performance_review:who_to_review")} size={"md"} />
            <Stack gap={"md"}>
              <RuleGroupsEditor
                control={control}
                fieldName={"participants_rule_groups"}
                setValue={setValue}
                place={RuleRegistryPlace.performanceReviewParticipants}
                error={errors.participants_rule_groups?.message}
                conditionTypes={participantConditionTypes.conditionTypes}
                maxCardsPerRow={2}
              />
            </Stack>
          </Stack>
          <Stack className={"card-panel"} gap={"2xl"}>
            <Title header={t("common:settings")} size={"md"} />
            <Stack gap={"sm"}>
              <Switch
                checked={isTimeFrame}
                onChange={(value) => {
                  setIsTimeFrame(value);
                  if (!value) {
                    setValue("date_starts_at", null);
                    setValue("date_ends_at", null);
                  }
                }}
                title={t("common:set_timeframe")}
              />
              {isTimeFrame && (
                <Box className={"grid grid-cols-2 gap-(--gap-sm)"}>
                  <Controller
                    control={control}
                    name="date_starts_at"
                    render={({ field: { value, onChange } }) => (
                      <Datepicker
                        value={value}
                        onChange={onChange}
                        label={t("common:date_from")}
                        isClearable={false}
                        error={errors.date_starts_at?.message}
                      />
                    )}
                  />
                  <Controller
                    control={control}
                    name="date_ends_at"
                    render={({ field: { value, onChange } }) => (
                      <Datepicker
                        value={value}
                        onChange={onChange}
                        label={t("common:date_to")}
                        isClearable={false}
                        error={errors.date_ends_at?.message}
                      />
                    )}
                  />
                </Box>
              )}
            </Stack>
          </Stack>
          {canManageOwners && (
            <Stack className={"card-panel"} gap={"2xl"}>
              <Title header={t("common:co_owners")} size={"md"} />
              <Stack gap={"md"}>
                <Switch
                  checked={useInviteCoOwners}
                  onChange={(value) => {
                    setUseInviteCoOwners(value);
                  }}
                  title={t("performance_review:invite_co_owners_title")}
                  description={t("performance_review:invite_co_owners_description")}
                />
                {useInviteCoOwners && (
                  <Controller
                    render={({ field: { value, onChange } }) => (
                      <Multiselect
                        label={t("common:co_owners")}
                        emptyTitle={t("common:select")}
                        values={value ?? []}
                        options={ownerOptions}
                        searchable
                        loadOptionsOnOpen
                        searchOptions={employeesOwnersSearchOptions}
                        isLoading={isEmployeesOwnerIsLoading}
                        onSearchOptions={(value) => {
                          setEmployeeOwnersFilter({
                            ...employeeOwnersFilter,
                            text: value,
                          });
                        }}
                        onChange={onChange}
                        error={errors.owner_ids?.message}
                      />
                    )}
                    control={control}
                    name={"owner_ids"}
                  />
                )}
              </Stack>
            </Stack>
          )}
          <Button type={"submit"} size={"lg"} isLoading={reviewUpdate.isPending}>
            {t("common:continue")}
          </Button>
        </Stack>
      </form>
    </FullScreenPage>
  );
};
